/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { createRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router';

import DiseaseApi from 'api/DiseaseApi';
import { InsightsState } from 'app-redux/InsightsState';
import { HistoryPanelFilterOptionsValue } from 'models/FilterOptions';
import { sxtheme } from 'utils/cssHelpers';
import { MapAggregationCat2Request } from 'utils/modelHelpers';
import { isMobile } from 'utils/responsive';

import { ViewPanel } from 'components/_controls/ViewPanel';

import diseaseActivityListSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityList.selectors';
import { setDiseaseActivityUpdatesFilter } from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.slice';
import { closePanel, openPanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import {
  minimizePanel,
  PanelType,
  PanelVisibility,
} from 'diseaseActivityPanels/diseaseActivityPanels.slice';
import locationSelectors from 'locations/locations.selectors';

import DiseaseActivityCard from './DiseaseActivityCards/DiseaseActivityCard';
import { DiseaseActivityCardWrtAoi } from './DiseaseActivityCards/DiseaseActivityCardWrtAoi';
import DiseaseActivityDetails from './DiseaseActivityDetails';
import DiseaseActivityListTabs from './DiseaseActivityList/DiseaseActivityListTabs';
import { RecentActivityCardGlobal } from './DiseaseActivityList/RecentActivityCardGlobal';
import { RecentActivityCardWrtAoi } from './DiseaseActivityList/RecentActivityCardWrtAoi';
import RiskCardForDisease from './DiseaseActivityList/RiskCardForDisease';
import DiseaseActivityUpdates from './DiseaseActivityUpdates';

const DiseaseActivityList: React.FC = () => {
  const dispatch = useDispatch();
  const isMobileDevice = isMobile(useBreakpointIndex());

  const eventTitle = useSelector(diseaseActivityListSelectors.eventTitle);
  const hasError = useSelector(diseaseActivityListSelectors.hasError);
  const isLoading = useSelector(diseaseActivityListSelectors.isLoadingWrtPreviousPanel);
  const disactIds = useSelector(diseaseActivityPanelsSelectors.disacParams);
  const activePanel = useSelector(diseaseActivityPanelsSelectors.activePanel);
  const isHistorySelected = useSelector(
    diseaseActivityPanelsSelectors.isHistorySelectedFromActivityList
  );
  const selectedLocationName = useSelector(locationSelectors.getSelectedLocationTitle);

  const totalCaseCounts = useSelector(diseaseActivityListSelectors.getTotalCaseCounts);
  const firstActivityUpdateDate = useSelector(
    diseaseActivityListSelectors.getFirstActivityUpdateDateStr
  );

  const { canClose, isMinimized, isOpen } = useSelector<InsightsState, PanelVisibility>(
    diseaseActivityPanelsSelectors.getPanelVisibility(PanelType.DiseaseActivityListPanel)
  );

  const onMinimize = (flag: boolean): void => {
    dispatch(minimizePanel(PanelType.DiseaseActivityListPanel, flag));
  };

  const openHistoryPanel = (filter: HistoryPanelFilterOptionsValue, activityDate?: string) => {
    dispatch(setDiseaseActivityUpdatesFilter(filter));
    dispatch(
      openPanel({
        panel: 'DiseaseActivityUpdatesFromList',
        activityDate: activityDate,
      })
    );
  };

  const isGlobal = useSelector(diseaseActivityPanelsSelectors.isGlobalSelected);
  const disacParams = useSelector(diseaseActivityPanelsSelectors.disacParams);

  const parsedUrl: URL = new URL(window.location.href);
  const [isActivityAnchor, setIsActivityAnchor] = useState(parsedUrl.searchParams.has('activity'));
  const refScrollToDiseaseActivityListTabs = React.useRef<HTMLDivElement>();
  const diseaseActivityListTabsAnchorRef = createRef<HTMLElement>();

  useEffect(() => {
    if (isActivityAnchor && diseaseActivityListTabsAnchorRef.current) {
      refScrollToDiseaseActivityListTabs &&
        refScrollToDiseaseActivityListTabs.current.scrollIntoView(true);
      setIsActivityAnchor(false);
      parsedUrl.searchParams.delete('activity');
      window.history.replaceState({}, document.title, parsedUrl.pathname);
    }
  }, [diseaseActivityListTabsAnchorRef]);

  const showPanel =
    (isMobileDevice && activePanel === PanelType.DiseaseActivityListPanel) ||
    (!isMobileDevice && isOpen);

  return (
    <>
      {showPanel && (
        <ViewPanel
          title={
            isLoading
              ? 'Loading...'
              : eventTitle || (hasError ? 'Something went wrong' : 'Loading...')
          }
          subtitleMobile={selectedLocationName || 'Loading...'}
          isLoading={isLoading}
          hasError={hasError}
          canClose={canClose}
          onClose={() => dispatch(closePanel(PanelType.DiseaseActivityListPanel))}
          isMinimized={isMinimized}
          onMinimize={onMinimize}
        >
          <>
            <div
              sx={{
                p: '16px',
                bg: sxtheme(t => t.colors.deepSea10),
              }}
            >
              {isGlobal ? (
                <>
                  <DiseaseActivityCard
                    isGlobal
                    totalCaseCounts={totalCaseCounts}
                    firstActivityUpdateDate={firstActivityUpdateDate}
                    isFooterActive={isHistorySelected}
                    onNeedApiGetDiseaseActivity={(startDate, endDate, aggregationCategory) =>
                      DiseaseApi.DiseaseController_GetTemporalActivity({
                        ...MapAggregationCat2Request[aggregationCategory],
                        diseaseId: disactIds.diseaseId,
                        speciesId: 1, // TODO: get corresponding species ID
                        activityStartDate: startDate ? startDate.toISOString() : undefined,
                        activityEndDate: endDate ? endDate.toISOString() : undefined,
                        descriptionCountForAggregationTime: 2,
                      })
                    }
                    onGoToActivityHistory={(date?: string) =>
                      openHistoryPanel('all-activity', date)
                    }
                  />
                  <RecentActivityCardGlobal
                    onGoToActivityHistory={(date?: string) =>
                      openHistoryPanel('with-updates-only', date)
                    }
                  />
                </>
              ) : (
                <>
                  <DiseaseActivityCardWrtAoi
                    totalCaseCounts={totalCaseCounts}
                    firstActivityUpdateDate={firstActivityUpdateDate}
                    isFooterActive={isHistorySelected}
                    onNeedApiGetDiseaseActivity={(startDate, endDate, aggregationCategory) =>
                      DiseaseApi.DiseaseController_GetTemporalActivity({
                        ...MapAggregationCat2Request[aggregationCategory],
                        diseaseId: disactIds.diseaseId,
                        speciesId: 1, // TODO: get corresponding species ID
                        locationId: disacParams?.userLocationId,
                        includeProximalActivity: true,
                        activityStartDate: startDate ? startDate.toISOString() : undefined,
                        activityEndDate: endDate ? endDate.toISOString() : undefined,
                        descriptionCountForAggregationTime: 2,
                      })
                    }
                    onGoToActivityHistory={(activityDateNew?: string) => {
                      dispatch(setDiseaseActivityUpdatesFilter('all-activity'));
                      dispatch(
                        openPanel({
                          panel: 'DiseaseActivityUpdatesFromList',
                          activityDate: activityDateNew,
                        })
                      );
                    }}
                  />
                  <RecentActivityCardWrtAoi
                    onGoToActivityHistory={(activityDateNew?: string) => {
                      dispatch(setDiseaseActivityUpdatesFilter('with-updates-only'));
                      dispatch(
                        openPanel({
                          panel: 'DiseaseActivityUpdatesFromList',
                          activityDate: activityDateNew,
                        })
                      );
                    }}
                  />
                </>
              )}

              <RiskCardForDisease />
            </div>
            <DiseaseActivityListTabs
              diseaseActivityListTabsRef={diseaseActivityListTabsAnchorRef}
              refScrollToTabs={refScrollToDiseaseActivityListTabs}
            />
          </>
        </ViewPanel>
      )}
      <Routes>
        <Route path="country/:countryId/*" element={<DiseaseActivityDetails />} />
        <Route path="activity" element={<DiseaseActivityUpdates />} />
      </Routes>
    </>
  );
};

export default DiseaseActivityList;
